import { Component } from './Component';
import { AppStore } from '../store/AppStore';
import type { MapView } from './MapView';
import { createLogger } from '../utils/logger';

const log = createLogger('BrickWallPanel');

export class BrickWallPanel extends Component {
  private headingEl!: HTMLHeadingElement;
  private listEl!: HTMLUListElement;

  constructor(private mapView: MapView) {
    super();
  }

  render(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'brick-wall-panel';
    el.setAttribute('data-testid', 'brick-wall-panel');
    el.style.display = 'none';
    el.innerHTML = `
      <h3 data-testid="brick-wall-heading">Brick walls (0)</h3>
      <ul data-testid="brick-wall-list" tabindex="0"></ul>
    `;
    this.headingEl = el.querySelector('h3')!;
    this.listEl = el.querySelector('ul')!;
    return el;
  }

  mount(parent: HTMLElement): void {
    super.mount(parent);
    this.track(AppStore.subscribe(() => this._update()));
    this._update();
    log.info('BrickWallPanel mounted');
  }

  private _update(): void {
    const { graph, branchPaths } = AppStore.getState();
    if (!graph) {
      this.el.style.display = 'none';
      return;
    }

    const brickWalls = [...graph.persons.values()].filter((p) => p.isBrickWall);
    if (brickWalls.length === 0) {
      this.el.style.display = 'none';
      return;
    }
    this.el.style.display = '';

    this.headingEl.textContent = `Brick walls (${brickWalls.length})`;
    this.listEl.innerHTML = '';

    // Map each brick wall to its branch color via the tip of the branch path.
    const brickWallColor = new Map<string, string>();
    for (const path of branchPaths) {
      const tipId = path.personIds[path.personIds.length - 1] ?? '';
      const tip = graph.persons.get(tipId);
      if (tip?.isBrickWall) {
        brickWallColor.set(tipId, path.branchColor);
      }
    }

    brickWalls.forEach((person, idx) => {
      const birth = person.events.find((e) => e.type === 'birth');
      const loc = birth?.location;
      const rawText = loc ? loc.raw : '(location unknown)';
      const canPan =
        loc !== undefined &&
        loc.geocoded &&
        loc.lat !== undefined &&
        loc.lng !== undefined &&
        Number.isFinite(loc.lat) &&
        Number.isFinite(loc.lng);

      const name = `${person.name.first} ${person.name.last}`.trim() || '(unknown)';

      const li = document.createElement('li');

      const color = brickWallColor.get(person.id);
      if (color) {
        const swatchEl = document.createElement('span');
        swatchEl.className = 'branch-swatch bw-swatch';
        swatchEl.setAttribute('aria-hidden', 'true');
        swatchEl.style.background = color;
        li.appendChild(swatchEl);
      }

      const nameSpan = document.createElement('span');
      nameSpan.className = 'bw-name';
      nameSpan.textContent = name;

      const placeSpan = document.createElement('span');
      placeSpan.className = 'bw-place';
      placeSpan.textContent = rawText;

      li.appendChild(nameSpan);
      li.appendChild(placeSpan);

      if (canPan) {
        const btn = document.createElement('button');
        btn.className = 'bw-pan-btn';
        btn.setAttribute('data-testid', `pan-to-${idx}`);
        btn.setAttribute('aria-label', `Pan to ${name}'s birth location`);
        btn.textContent = 'Pan';
        btn.addEventListener('click', () => {
          this.mapView.panTo(loc!.lat!, loc!.lng!);
        });
        li.appendChild(btn);
      }

      this.listEl.appendChild(li);
    });
  }
}
